import { createElement, isValidElement, type ComponentType, type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Text } from './Typography';
import type { IconVariant } from '@/components/icons';

type MetricTone = 'brand' | 'success' | 'warning' | 'info' | 'neutral';
type MetricIcon = ComponentType<{ className?: string; variant?: IconVariant }> | ReactNode;
type MetricAppearance = 'default' | 'minimal';

const TONE_STYLES: Record<
    MetricTone,
    {
        icon: string;
        meta: string;
    }
> = {
    brand: {
        icon: 'text-brand',
        meta: 'text-brand',
    },
    success: {
        icon: 'text-success',
        meta: 'text-success',
    },
    warning: {
        icon: 'text-warning',
        meta: 'text-warning',
    },
    info: {
        icon: 'text-info',
        meta: 'text-info',
    },
    neutral: {
        icon: 'text-foreground',
        meta: 'text-muted-foreground',
    },
};

interface MetricCardProps {
    title: ReactNode;
    value: ReactNode;
    icon?: MetricIcon;
    tone?: MetricTone;
    meta?: ReactNode;
    appearance?: MetricAppearance;
    className?: string;
}

function renderIcon(icon: MetricIcon) {
    if (isValidElement(icon)) return icon;

    // Stat tiles are an emphasis context — the icon is the tile's visual
    // anchor, so opt into the two-tone Bulk fill (ignored by non-Iconsax icons).
    return createElement(icon as ComponentType<{ className?: string; variant?: IconVariant }>, {
        className: 'h-10 w-10',
        variant: 'Bulk',
    });
}

export default function MetricCard({
    title,
    value,
    icon,
    tone = 'brand',
    meta,
    appearance = 'default',
    className,
}: MetricCardProps) {
    const styles = TONE_STYLES[tone];
    const isMinimal = appearance === 'minimal';

    if (isMinimal) {
        return (
            <Card
                className={cn('rounded-[20px] border border-border bg-card shadow-sm', className)}
            >
                <CardContent className="p-4">
                    <div>
                        <Text variant="label">{title}</Text>
                        <div className="mt-3 text-4xl font-semibold leading-none tracking-tight text-foreground sm:text-[2.75rem]">
                            {value}
                        </div>
                        {meta && (
                            <Text variant="caption" className="mt-2 text-[11px]">
                                {meta}
                            </Text>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            className={cn(
                'relative overflow-hidden rounded-[calc(var(--radius)+0.125rem)] border border-border/80 bg-card/95 shadow-elevation-1',
                className,
            )}
        >
            <CardContent className="relative flex items-start justify-between gap-4 p-6">
                <div className="space-y-2">
                    <Text variant="meta">{title}</Text>
                    <div className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">
                        {value}
                    </div>
                    {meta && (
                        <Text variant="meta" className={styles.meta}>
                            {meta}
                        </Text>
                    )}
                </div>
                {icon && (
                    <div className={cn('flex shrink-0 items-center justify-center', styles.icon)}>
                        {renderIcon(icon)}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
